import { fetchAuthSession } from "@aws-amplify/auth";
import { get } from "aws-amplify/api";

/**
 * Interface for usage data response
 */
export interface UsageData {
    userId?: string;
    month: string;
    count: number;
}

/**
 * Client for the Receipt Usage Tracking API
 */
export class ReceiptUsageClient {
    private readonly apiName: string;

    /**
     * Create a new Receipt Usage Client
     * @param apiName The name of the API in Amplify configuration
     */
    constructor(apiName: string = "Receipt Usage API") {
        this.apiName = apiName;
    }

    /**
     * Get usage count for a specific user in a month
     * @param userId The user ID
     * @param month Optional month in YYYY-MM format (defaults to current month)
     */
    async getUserUsage(userId: string, month?: string): Promise<UsageData> {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        const response = await get({
            apiName: this.apiName,
            path: `/usage/users/${userId}`,
            options: {
                queryParams: month ? { month } : undefined,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        }).response;

        return (await response.body.json()) as unknown as UsageData;
    }

    /**
     * Get usage count for all users in a month (tenant total)
     * @param month Optional month in YYYY-MM format (defaults to current month)
     */
    async getTenantUsage(month?: string): Promise<UsageData> {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();

        const response = await get({
            apiName: this.apiName,
            path: `/usage/tenant`,
            options: {
                queryParams: month ? { month } : undefined,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        }).response;

        return (await response.body.json()) as unknown as UsageData;
    }
}
